import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Package, Ruler, Scales, X } from '@phosphor-icons/react';
import styled from 'styled-components';
import { ItemImage, SkupremeModal } from '../../../components';
import {
  BoldText,
  CenterDropdownStyle,
  HoverBorderButton,
  MuteText,
  StyledButton,
} from '../../../elements';
import {
  CommonUtility,
  ErrorMessageConstant,
  ShippingSpeedOptions,
  theme,
  ToastMessage,
} from '../../../utility';
import { PackagingSearchInput } from './package-search';
import { PackagingService } from '../../../utility/services/packaging';
import { ConversionUtility } from '../../../utility/conversions';
import { sumContentsWeightInPounds } from '../../../utility/packaging/packed-weight';
import { ItemMiniInput } from '@page-components/shipments/Items/commonComponents';

const GreyBox = styled.div`
  background: ${({ theme }) => theme.colors.box};
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.colors.background};
  padding: 20px;
`;

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 40vh;
  overflow: auto;
`;

const WhiteBox = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 5px;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.colors.background};
`;

const DimensionContainer = styled.div`
  display: flex;
  column-gap: 5px;
  align-items: center;
`;

const RateContainer = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.box};
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-radius: 5px;
  height: 100%;
  width: 100%;
  cursor: pointer;
  border: 2px solid ${({ theme }) => theme.colors.white};

  &:hover {
    border: 2px solid ${({ theme }) => theme.colors.grey};
  }

  &.selected {
    border: 2px solid ${({ theme }) => theme.colors.primary};
    box-sizing: border-box;
  }
`;

const packageTypes = {
  custom: 'custom',
  mailer: 'mailer',
  shipsInProductPackaging: 'shipsInProductPackaging',
  strappedBundle: 'strappedBundle',
  shipInMasterCarton: 'shipInMasterCarton',
};

export function PackagingConfirmationPopup({
  closeAndFulfill,
  currentAllocation,
  fulfillItem,
  onClose,
  onLabelGenerated,
  onPalletLabelGenerated,
  open,
  order,
  packages,
  processing,
  toteId,
  warehouseId,
}) {
  const [packageIndex, setPackageIndex] = useState(0);
  const [packagingMaterial, setPackagingMaterial] = useState(null);
  const [type, setType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [configureServiceMode, setConfigureServiceMode] = useState(false);
  const [configureOrderSpeed, setConfigureOrderSpeed] = useState('Standard');
  const [rates, setRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [selectedRate, setSelectedRate] = useState(-1);
  const [boxLength, setBoxLength] = useState('');
  const [boxWidth, setBoxWidth] = useState('');
  const [boxHeight, setBoxHeight] = useState('');

  const hasCurrentAllocation = useMemo(
    () => CommonUtility.isValidObject(currentAllocation),
    [currentAllocation],
  );

  const currentPackage = packages?.[packageIndex] || {};

  const totalWeight = useMemo(() => {
    const entries = hasCurrentAllocation
      ? [
          {
            weight: currentAllocation?.productId?.package_dimensions?.weight,
            qty: currentAllocation?.quantity,
          },
        ]
      : (currentPackage?.packedItems || []).map(item => ({
          weight: item?.productId?.package_dimensions?.weight,
          qty: item?.quantity,
        }));

    return {
      weight: ConversionUtility.convertToPoundsAndOunces(
        sumContentsWeightInPounds(entries),
      ),
      unit: 'lb',
    };
  }, [
    currentAllocation?.productId?.package_dimensions?.weight,
    currentAllocation?.quantity,
    currentPackage?.packedItems,
    hasCurrentAllocation,
  ]);

  const options = useMemo(() => {
    const arr = [
      {
        text: 'Packaging Boxes',
        value: packageTypes.custom,
        key: packageTypes.custom,
      },
      {
        text: 'Mailer',
        value: packageTypes.mailer,
        key: packageTypes.mailer,
      },
    ];
    let isSingleItem = false;

    if (hasCurrentAllocation) {
      isSingleItem = currentAllocation?.quantity === 1;
    } else {
      isSingleItem =
        currentPackage?.packedItems?.length === 1 &&
        currentPackage?.packedItems[0]?.quantity === 1;
    }

    if (isSingleItem) {
      arr.push({
        text: 'Ships in Product Packaging',
        value: packageTypes.shipsInProductPackaging,
        key: packageTypes.shipsInProductPackaging,
      });
    } else {
      arr.push({
        text: 'Ship In Master Carton',
        key: packageTypes.shipInMasterCarton,
        value: packageTypes.shipInMasterCarton,
      });
    }

    arr.push({
      text: 'Strapped Bundle',
      value: packageTypes.strappedBundle,
      key: packageTypes.strappedBundle,
    });

    return arr;
  }, [currentPackage?.packedItems, hasCurrentAllocation]);

  const showPackagingMaterial = useMemo(() => type === 'custom', [type]);

  const buildItemsArray = useCallback(() => {
    if (hasCurrentAllocation) {
      return [
        {
          qty: currentAllocation?.quantity,
          itemId: currentAllocation?.productId?._id,
        },
      ];
    }

    const itemsArray =
      currentPackage?.items?.map(item => ({
        qty: item.qty,
        itemId: item.item_id,
      })) || [];

    return itemsArray?.filter(item => item.qty > 0) || [];
  }, [hasCurrentAllocation, currentAllocation, currentPackage?.items]);

  const getCurrentLabel = useCallback(() => {
    if (hasCurrentAllocation) {
      return currentAllocation?.shipping?.labels?.[0];
    }
    return currentPackage?.label;
  }, [hasCurrentAllocation, currentAllocation, currentPackage?.label]);

  const packageRate = useMemo(() => {
    if (CommonUtility.isValidObject(rates?.packages?.[0])) {
      return rates?.packages?.[0];
    }
    return null;
  }, [rates]);

  const filteredRates = useMemo(
    () => packageRate?.rates?.filter(x => !x.error) || [],
    [packageRate],
  );

  const checkIfLabelCorrectionNeeded = () => {
    let label = null;
    if (hasCurrentAllocation) {
      label = currentAllocation?.shipping?.labels?.[0];
    } else {
      label = currentPackage?.label;
    }

    const metadata = label?.metadata || {};
    const currentMaterialId = label?.packagingMaterial?._id;
    const selectedMaterialId = packagingMaterial?._id;

    const previousType = metadata.mailer
      ? packageTypes.mailer
      : metadata.strapped
        ? packageTypes.strappedBundle
        : metadata.shipsInProductPackaging
          ? packageTypes.shipsInProductPackaging
          : metadata.shipInMasterCarton
            ? packageTypes.shipInMasterCarton
            : packageTypes.custom;

    if (previousType !== type) {
      if (
        [previousType, type].includes(packageTypes.mailer) &&
        [previousType, type].includes(packageTypes.custom)
      ) {
        if (
          type === packageTypes.mailer &&
          packagingMaterial?.packagingType !== 'Envelope'
        ) {
          return true;
        }

        return currentMaterialId !== selectedMaterialId;
      }
      return true;
    }

    if (type === 'custom') {
      return currentMaterialId !== selectedMaterialId;
    }

    return false;
  };

  const prepareConfirmationPaylaod = () => {
    const itemsArray = buildItemsArray();
    const label = getCurrentLabel();

    const orderSpeed = configureServiceMode
      ? configureOrderSpeed
      : label?.metadata?.shipping_speed || 'Standard';

    let payload = {
      orderSpeed,
      labelId: label?._id,
    };

    if (hasCurrentAllocation) {
      payload = {
        ...payload,
        orderId: currentAllocation?.order?._id,
        fulfillmentProviderId: currentAllocation?.order?.fulfillment_provider_id,
        allocationId: currentAllocation.allocationId?._id,
      };
    } else {
      payload = {
        ...payload,
        orderId: order?._id,
        fulfillmentProviderId: order?.fulfillment_provider_id,
        toteId,
      };
    }

    if (type === packageTypes.mailer) {
      payload.mailer = true;
      payload.items = itemsArray;
    } else if (type === packageTypes.strappedBundle) {
      payload.strappedBundle = true;
      payload.items = itemsArray;
    } else if (type === packageTypes.shipsInProductPackaging) {
      payload.shipsInProductPackaging = true;
      payload.items = itemsArray;
    } else if (type === packageTypes.shipInMasterCarton) {
      payload.shipInMasterCarton = true;
      payload.items = itemsArray;
    } else {
      payload.packages = [
        {
          packagingMaterialId: packagingMaterial?._id,
          pounds: Number(totalWeight?.weight?.pounds || 0),
          ounces: Number(totalWeight?.weight?.ounces || 0),
          items: itemsArray,
        },
      ];
    }

    if (
      configureServiceMode &&
      selectedRate >= 0 &&
      CommonUtility.isValidArray(filteredRates) &&
      filteredRates[selectedRate]
    ) {
      const rate = filteredRates[selectedRate];
      if (type === packageTypes.custom && payload.packages?.[0]) {
        payload.packages[0].provider = rate.provider?.value;
        payload.packages[0].service = rate.service?.value;
        payload.packages[0].package = rate.package?.value;
      } else if (type !== packageTypes.custom) {
        payload.packages = [
          {
            pounds: Number(totalWeight?.weight?.pounds || 0),
            ounces: Number(totalWeight?.weight?.ounces || 0),
            length: Number(boxLength),
            width: Number(boxWidth),
            height: Number(boxHeight),
            items: itemsArray,
            provider: rate.provider?.value,
            service: rate.service?.value,
            package: rate.package?.value,
          },
        ];
      }
    }

    return payload;
  };

  const fetchRates = async () => {
    if (type === packageTypes.custom && !packagingMaterial) {
      ToastMessage.error('Please select a packaging material');
      return;
    }
    if (type !== packageTypes.custom) {
      const L = Number(boxLength);
      const W = Number(boxWidth);
      const H = Number(boxHeight);
      if (!(L > 0 && W > 0 && H > 0)) {
        ToastMessage.error('Enter length, width, and height (inches)');
        return;
      }
    }

    const itemsArray = buildItemsArray();
    const pkg =
      type === packageTypes.custom
        ? {
            packagingMaterialId: packagingMaterial?._id,
            pounds: Number(totalWeight?.weight?.pounds || 0),
            ounces: Number(totalWeight?.weight?.ounces || 0),
            items: itemsArray,
          }
        : {
            pounds: Number(totalWeight?.weight?.pounds || 0),
            ounces: Number(totalWeight?.weight?.ounces || 0),
            length: Number(boxLength),
            width: Number(boxWidth),
            height: Number(boxHeight),
            items: itemsArray,
          };

    const body = {
      orderId: hasCurrentAllocation ? currentAllocation?.order?._id : order?._id,
      fulfillmentProviderId: hasCurrentAllocation
        ? currentAllocation?.order?.fulfillment_provider_id
        : order?.fulfillment_provider_id,
      orderSpeed: configureOrderSpeed,
      packages: [pkg],
    };

    try {
      setRatesLoading(true);
      const rateRes = await PackagingService.shippingRates(body);
      setRates(rateRes);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setRatesLoading(false);
    }
  };

  const toggleConfigureService = () => {
    setConfigureServiceMode(prev => {
      if (!prev) {
        const label = hasCurrentAllocation
          ? currentAllocation?.shipping?.labels?.[0]
          : currentPackage?.label;
        setConfigureOrderSpeed(label?.metadata?.shipping_speed || 'Standard');
        return true;
      }
      setRates(null);
      setSelectedRate(-1);
      return false;
    });
  };

  const resetConfigureServiceState = () => {
    setConfigureServiceMode(false);
    setRates(null);
    setSelectedRate(-1);
    setBoxLength('');
    setBoxWidth('');
    setBoxHeight('');
  };

  const onConfirm = async () => {
    try {
      setLoading(true);
      const configureCorrection =
        configureServiceMode &&
        selectedRate >= 0 &&
        CommonUtility.isValidArray(filteredRates) &&
        filteredRates.length > 0;

      const shouldCorrectLabel = checkIfLabelCorrectionNeeded() || configureCorrection;

      let res = null;

      if (shouldCorrectLabel) {
        const payload = prepareConfirmationPaylaod();
        res = await PackagingService.lableCorrection(payload);
        if (CommonUtility.isValidObject(res?.data)) {
          if (hasCurrentAllocation) {
            onPalletLabelGenerated(currentAllocation.allocationId?._id, res?.data);
          } else {
            onLabelGenerated(packageIndex, res?.data);
          }
        } else {
          ToastMessage.error(ErrorMessageConstant.defaultAPIError);
        }
      }

      if (packages.length > 1 && packageIndex < packages.length - 1) {
        setPackageIndex(packageIndex => packageIndex + 1);
        setType(null);
        setPackagingMaterial(null);
        resetConfigureServiceState();
      } else if (hasCurrentAllocation) {
        fulfillItem(currentAllocation?.productId?.upc, res?.data);
      } else {
        closeAndFulfill();
      }
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;

  useEffect(() => {
    if (!currentPackage && !hasCurrentAllocation) return;

    let label = null;
    if (hasCurrentAllocation) {
      label = currentAllocation?.shipping?.labels?.[0];
    } else {
      label = currentPackage?.label;
    }

    const metadata = label?.metadata;

    setPackagingMaterial(label?.packagingMaterial);

    if (CommonUtility.isValidObject(metadata)) {
      if (metadata?.mailer) {
        setType('mailer');
        return;
      }

      if (metadata?.strapped) {
        setType('strappedBundle');
        return;
      }

      if (metadata?.shipInMasterCarton) {
        setType('shipInMasterCarton');
        return;
      }
    }

    setType('custom');
  }, [
    currentPackage?.label?.metadata,
    currentPackage?.label?.packagingMaterial,
    hasCurrentAllocation,
    currentAllocation?.shipping?.labels,
  ]);

  useEffect(() => {
    if (!open) {
      setConfigureServiceMode(false);
      setRates(null);
      setSelectedRate(-1);
      setBoxLength('');
      setBoxWidth('');
      setBoxHeight('');
      setRatesLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!configureServiceMode || !open) return;
    setRates(null);
    setSelectedRate(-1);
  }, [
    configureServiceMode,
    open,
    type,
    packagingMaterial?._id,
    boxLength,
    boxWidth,
    boxHeight,
    configureOrderSpeed,
  ]);

  useEffect(() => {
    if (!configureServiceMode) return;
    if (!CommonUtility.isValidArray(filteredRates) || !filteredRates.length) {
      setSelectedRate(-1);
      return;
    }
    let min = Infinity;
    let indexToSelect = -1;
    filteredRates.forEach((rate, i) => {
      if (!rate.error && rate.rate < min) {
        min = rate.rate;
        indexToSelect = i;
      }
    });
    setSelectedRate(indexToSelect);
  }, [configureServiceMode, packageRate, filteredRates]);

  const isValid = useMemo(() => {
    if (!type) return false;
    if (type === 'custom' && !packagingMaterial) return false;
    if (configureServiceMode) {
      if (type !== packageTypes.custom) {
        const L = Number(boxLength);
        const W = Number(boxWidth);
        const H = Number(boxHeight);
        if (!(L > 0 && W > 0 && H > 0)) return false;
      }
      if (
        !CommonUtility.isValidArray(filteredRates) ||
        filteredRates.length === 0 ||
        selectedRate < 0
      ) {
        return false;
      }
    }
    return true;
  }, [
    type,
    packagingMaterial,
    configureServiceMode,
    boxLength,
    boxWidth,
    boxHeight,
    filteredRates,
    selectedRate,
  ]);

  useEffect(() => {
    const handleEnter = e => {
      if (e.key === 'Enter') {
        if (!open) return;
        if (isValid && !loading && !processing && !ratesLoading) {
          onConfirmRef.current();
        }
      }
    };

    if (open) {
      window.addEventListener('keydown', handleEnter);
    }

    return () => {
      window.removeEventListener('keydown', handleEnter);
    };
  }, [open, isValid, loading, processing, ratesLoading]);

  return (
    <SkupremeModal
      open={open}
      title="Confirm Package Configurations"
      size="small"
      onClose={onClose}
    >
      <GreyBox className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <Package size={24} color={theme.colors.grey} />
            <h3 className="m-0 ml-2">Package {packageIndex + 1}</h3>
          </div>
          <div className="d-flex align-items-center">
            <Scales size={24} color={theme.colors.grey} />
            <DimensionContainer className="m-0 ml-2">
              <span>{CommonUtility.roundNumber(totalWeight?.weight?.pounds || 0)}</span>
              <MuteText>lbs</MuteText>
              <span>{CommonUtility.roundNumber(totalWeight?.weight?.ounces || 0)}</span>
              <MuteText>oz</MuteText>
            </DimensionContainer>
          </div>
        </div>
        {currentAllocation ? (
          <ItemRenderer item={currentAllocation} />
        ) : (
          <ItemsContainer>
            {currentPackage?.packedItems?.map(item => (
              <ItemRenderer key={item?._id} item={item} />
            ))}
          </ItemsContainer>
        )}
        <div className="mt-3">
          <MuteText>Package Configurations</MuteText>
          <CenterDropdownStyle
            placeholder="Select Type"
            className="mt-2"
            options={options}
            selection
            value={type}
            onChange={(e, { value }) => setType(value)}
          />
          {showPackagingMaterial && (
            <PackagingSearchInput
              inputClassName="w-100 my-3"
              warehouseId={warehouseId}
              onResultClick={setPackagingMaterial}
            />
          )}
          {showPackagingMaterial && packagingMaterial && (
            <WhiteBox>
              <div className="d-flex flex-column">
                <span>{packagingMaterial?.name}</span>
                <MuteText>{packagingMaterial?.packagingType}</MuteText>
              </div>
              <div>
                <DimensionContainer>
                  <Ruler size={14} color={theme.colors.grey} />
                  <span>{packagingMaterial?.dimensions?.length}</span>
                  <MuteText>x</MuteText>
                  <span>{packagingMaterial?.dimensions?.width}</span>
                  <MuteText>x</MuteText>
                  <span>{packagingMaterial?.dimensions?.height}</span>
                  <MuteText>({packagingMaterial?.dimensions?.uom})</MuteText>
                </DimensionContainer>
                <DimensionContainer>
                  <Scales size={14} color={theme.colors.grey} />
                  <span className="ml-1">{packagingMaterial?.weight?.value}</span>
                  <MuteText>{packagingMaterial?.weight?.uom}</MuteText>
                </DimensionContainer>
              </div>
            </WhiteBox>
          )}
          {configureServiceMode && (
            <div className="mt-2">
              <MuteText>Shipping speed</MuteText>
              <CenterDropdownStyle
                placeholder="Speed"
                className="mt-2"
                options={ShippingSpeedOptions}
                selection
                value={configureOrderSpeed}
                onChange={(e, { value }) => setConfigureOrderSpeed(value)}
              />
              {type !== packageTypes.custom && (
                <div className="mt-2">
                  <MuteText className="mt-3 d-block">Box size (in.)</MuteText>
                  <div className="mt-2 d-flex align-items-center">
                    <ItemMiniInput
                      type="number"
                      min={0}
                      step="any"
                      value={boxLength}
                      onChange={value => setBoxLength(value)}
                      placeholder="L"
                      noMax
                    />
                    <X className="icon mx-1" size={16} />
                    <ItemMiniInput
                      type="number"
                      min={0}
                      step="any"
                      value={boxWidth}
                      onChange={value => setBoxWidth(value)}
                      placeholder="W"
                      noMax
                    />
                    <X className="icon mx-1" size={16} />
                    <ItemMiniInput
                      type="number"
                      min={0}
                      step="any"
                      value={boxHeight}
                      onChange={value => setBoxHeight(value)}
                      placeholder="H"
                      noMax
                    />
                  </div>
                </div>
              )}
              <div className="mt-3 d-flex justify-content-end">
                <StyledButton
                  type="button"
                  loading={ratesLoading}
                  disabled={
                    !type ||
                    (type === packageTypes.custom && !packagingMaterial) ||
                    (type !== packageTypes.custom &&
                      !(
                        Number(boxLength) > 0 &&
                        Number(boxWidth) > 0 &&
                        Number(boxHeight) > 0
                      ))
                  }
                  onClick={fetchRates}
                >
                  Get rates
                </StyledButton>
              </div>
              {CommonUtility.isValidArray(filteredRates) && filteredRates.length > 0 && (
                <div className="row mt-2 mx-0">
                  {filteredRates.map((rate, i) => (
                    <div
                      key={`${rate?.provider?.value}-${rate?.service?.value}-${i}`}
                      className="col-6 mt-2 pl-0 pr-0"
                      role="presentation"
                      onClick={() => setSelectedRate(i)}
                    >
                      <RateContainer
                        className={`${selectedRate === i ? 'selected' : ''} ${
                          i % 2 === 0 ? '' : 'ml-2'
                        }`}
                      >
                        <div className="d-flex flex-column">
                          <BoldText>{rate?.provider?.key}</BoldText>
                          <MuteText>{rate?.service?.key}</MuteText>
                        </div>
                        <span>{CommonUtility.currencyFormat(rate.rate)}</span>
                      </RateContainer>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </GreyBox>
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        <HoverBorderButton type="button" onClick={toggleConfigureService}>
          {configureServiceMode ? 'Simple confirm' : 'Configure service'}
        </HoverBorderButton>
        <div className="d-flex align-items-center">
          <HoverBorderButton onClick={onClose}>Cancel</HoverBorderButton>
          <StyledButton
            loading={loading || processing}
            disabled={!isValid}
            className="ml-2"
            onClick={onConfirm}
          >
            {packages?.length > 1 ? 'Next Package' : 'Confirm'}
          </StyledButton>
        </div>
      </div>
    </SkupremeModal>
  );
}

const ClippedMuteText = styled(MuteText)`
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

function ItemRenderer({ item }) {
  return (
    <div className="d-flex">
      <ItemImage
        item={item?.productId}
        displayImageKey="display_image"
        imageKey="p_images"
        size="mini"
      />
      <div className="d-flex flex-column ml-2">
        <ClippedMuteText>{item?.productId?.p_title}</ClippedMuteText>
        <BoldText>{item?.productId?.p_sku}</BoldText>
      </div>
    </div>
  );
}
